import React, { useState } from 'react'

import { keys, map } from 'ramda'
import * as R from 'ramda'

import {
  Button,
  Flex,
  Modal,
  RadioGroup,
  RadioItem,
  styled,
  Textarea,
  useToast,
} from '@driftt/dds'

import { trackEvent } from 'utils/tracking'

import { EASE_OF_USE, SENTIMENT } from './FeedbackModal.constants'
import type { FeedbackModalProps } from './FeedbackModal.types'

const generateOptions = (options: object) =>
  map(
    (key) => <RadioItem key={key} value={key} label={options[key]} />,
    keys(options)
  )

export const FeedbackRadioGroup = styled(RadioGroup, {
  maxWidth: 'unset',
})

export const FeedbackModal = ({
  title = 'Give feedback',
  sentimentQuestionLabel = 'How would you feel if you could no longer use this report?',
  easeOfUseQuestionLabel = 'How difficult or easy is it to use this report?',
  feedbackQuestionLabel = 'How can we make Drift’s reporting even better for you? (Optional)',
  open,
  onClose,
  trackingEventName,
}: FeedbackModalProps) => {
  const triggerToast = useToast()

  const [sentiment, setSentiment] = useState('')
  const [easeOfUse, setEaseOfUse] = useState('')
  const [comments, setComments] = useState('')

  const handleSubmit = () => {
    const hasFeedback = R.any(R.complement(R.isEmpty), [
      sentiment,
      easeOfUse,
      comments,
    ])

    if (!hasFeedback) {
      onClose()
    } else {
      trackEvent(trackingEventName, {
        comments: comments ?? null,
        sentiment,
        easeOfUse,
      })
      triggerToast({
        message: 'Feedback successfully submitted. Thank you!',
        variant: 'success',
      })
      onClose()
    }
  }

  return (
    <Modal.Root open={open} onClose={onClose}>
      <Modal.Dialog size="large" variant="info" fullWidth>
        <Modal.Title title={title} />
        <Modal.Content as={Flex} gap={2} direction="column" fullWidth>
          <FeedbackRadioGroup
            label={easeOfUseQuestionLabel}
            value={easeOfUse}
            onValueChange={setEaseOfUse}
          >
            {generateOptions(EASE_OF_USE)}
          </FeedbackRadioGroup>
          <FeedbackRadioGroup
            label={sentimentQuestionLabel}
            value={sentiment}
            onValueChange={setSentiment}
          >
            {generateOptions(SENTIMENT)}
          </FeedbackRadioGroup>
          <Textarea
            size="full-width"
            label={feedbackQuestionLabel}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="It would be great if I could..."
          />
        </Modal.Content>
        <Modal.Actions>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Modal.Actions>
      </Modal.Dialog>
    </Modal.Root>
  )
}
